// v1.11: 信保 (SINOSURE) 报案向导
// 设计文档: docs/ANTI-FRAUD.md Part 6
//
// 流程:
//   1. 选 4 种 incidentType
//   2. 填案件金额 / 案件说明
//   3. 加时间线事件 (日期 + 类型 + 描述)
//   4. 加证据 (类型 + 名称 + 位置)
//   5. 生成 markdown → 下载 .md
//
// 提交后:
//   - 后端 generateSinosureReport() 拼装 5 段 markdown
//   - 写 compliance_event (type=sinosure_report)
//   - 前端下载 .md (PDF v1.12)
import { useState } from 'react';
import {
  type SinosureEvidence,
  type SinosureIncidentType,
  type SinosureTimelineEvent,
  collectionApi,
} from '../lib/api';
import { Icon } from './Icon';
import { Button } from './ui/Button';

const INCIDENT_META: Array<{
  key: SinosureIncidentType;
  label: string;
  icon: string;
  hint: string;
}> = [
  {
    key: 'late_payment',
    label: '客户拖欠 30-90 天',
    icon: 'clock',
    hint: '正常下单 → 出货 → 尾款未到 (Part 6.1)',
  },
  {
    key: 'account_hijack',
    label: '黑客拦截邮件',
    icon: 'siren',
    hint: '客户发"换账户"邮件, 99% 是骗子 (Part 6.2)',
  },
  {
    key: 'cargo_released',
    label: '货已被提走但未付款',
    icon: 'truck',
    hint: '电放 / 改收货人, 100% 追回概率低 (Part 6.3)',
  },
  {
    key: 'fake_buyer',
    label: '假冒买方 / 空壳公司',
    icon: 'user',
    hint: '工商 0 注册资本 / 拒接视频 / 多家公司同 IP',
  },
];

const EVIDENCE_TYPES: Array<{ value: SinosureEvidence['type']; label: string }> = [
  { value: 'contract', label: '销售合同' },
  { value: 'pi', label: '形式发票 PI' },
  { value: 'bank_slip', label: '银行水单' },
  { value: 'email', label: '邮件往来' },
  { value: 'b_l', label: '提单 B/L' },
  { value: 'chat_log', label: '聊天记录' },
  { value: 'other', label: '其他' },
];

interface SinosureReportWizardProps {
  orderId: string;
  customerId: string;
  /** 默认填的金额 (USD) — 通常 = order.totalAmountCents / 100 */
  defaultAmountUsd: number;
  /** 默认填的币种 (从 order) */
  currency: string;
  onToast: (kind: 'success' | 'error', msg: string) => void;
  onClose: () => void;
}

export function SinosureReportWizard({
  orderId,
  customerId,
  defaultAmountUsd,
  currency,
  onToast,
  onClose,
}: SinosureReportWizardProps) {
  const [incidentType, setIncidentType] = useState<SinosureIncidentType>('late_payment');
  const [amountUsd, setAmountUsd] = useState<number>(defaultAmountUsd);
  const [caseNote, setCaseNote] = useState('');
  const [timeline, setTimeline] = useState<SinosureTimelineEvent[]>([
    { date: new Date().toISOString().slice(0, 10), type: '报案日', description: '启动信保报案' },
  ]);
  const [evidence, setEvidence] = useState<SinosureEvidence[]>([
    { name: 'PI / 形式发票', type: 'pi' },
  ]);
  const [report, setReport] = useState<{
    markdown: string;
    filename: string;
    incidentLabel: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 币种换算 (简化: 假定订单币种 = USD, 非 USD 让用户自己改)
  const isForeignCurrency = currency !== 'USD';

  function addTimelineRow() {
    setTimeline((s) => [
      ...s,
      { date: new Date().toISOString().slice(0, 10), type: '', description: '' },
    ]);
  }

  function updateTimeline(i: number, patch: Partial<SinosureTimelineEvent>) {
    setTimeline((s) => s.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  }

  function removeTimeline(i: number) {
    setTimeline((s) => s.filter((_, idx) => idx !== i));
  }

  function addEvidenceRow() {
    setEvidence((s) => [...s, { name: '', type: 'other' }]);
  }

  function updateEvidence(i: number, patch: Partial<SinosureEvidence>) {
    setEvidence((s) => s.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  }

  function removeEvidence(i: number) {
    setEvidence((s) => s.filter((_, idx) => idx !== i));
  }

  async function handleGenerate() {
    if (amountUsd <= 0) {
      onToast('error', '金额必须大于 0');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await collectionApi.sinosureReport({
        orderId,
        customerId,
        incidentType,
        amountUsd,
        timeline: timeline.filter((t) => t.date && t.type),
        evidence: evidence.filter((e) => e.name),
        caseNote: caseNote.trim() || undefined,
      });
      setReport({
        markdown: data.markdown,
        filename: data.filename,
        incidentLabel: data.incidentLabel,
      });
      onToast('success', '信保报案材料已生成');
    } catch (e) {
      const msg = (e as Error).message;
      setError(msg);
      onToast('error', `生成失败: ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  function handleDownload() {
    if (!report) return;
    const blob = new Blob([report.markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = report.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-4 sm:pt-12 px-3 pb-6 overflow-y-auto">
      <div className="bg-surface border border-line rounded-md w-full max-w-3xl shadow-lg animate-fade-up">
        {/* 头部 */}
        <div className="flex items-center justify-between p-4 border-b border-line">
          <div className="flex items-center gap-2">
            <Icon name="siren" size="md" className="text-bad" />
            <h2 className="text-base font-semibold">生成信保 (SINOSURE) 报案材料</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-ink-3 hover:text-ink"
            aria-label="关闭"
          >
            <Icon name="x" size="md" />
          </button>
        </div>

        {/* 1. 选 incidentType */}
        <div className="p-4 border-b border-line">
          <div className="text-xs font-semibold text-ink-2 mb-2">1. 案件类型 (4 选 1)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {INCIDENT_META.map((m) => {
              const isActive = incidentType === m.key;
              return (
                <button
                  type="button"
                  key={m.key}
                  onClick={() => setIncidentType(m.key)}
                  className={`p-3 text-left rounded border text-sm ${
                    isActive ? 'border-bad bg-bad/5' : 'border-line hover:bg-surface-2'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon
                      name={m.icon}
                      size="sm"
                      className={isActive ? 'text-bad' : 'text-ink-3'}
                    />
                    <span className="font-medium">{m.label}</span>
                  </div>
                  <div className="text-[10px] text-ink-3 mt-1">{m.hint}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* 2. 金额 + 案件说明 */}
        <div className="p-4 border-b border-line">
          <div className="text-xs font-semibold text-ink-2 mb-2">2. 涉案金额 + 案件说明</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
            <label className="text-xs sm:col-span-1">
              <span className="text-ink-2">涉案金额 (USD)</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={amountUsd}
                onChange={(e) => setAmountUsd(Number(e.target.value))}
                className="mt-1 w-full px-2 py-1 border border-line rounded text-sm bg-surface"
              />
              {isForeignCurrency && (
                <div className="text-[10px] text-amber-600 mt-0.5">
                  ⚠️ 订单币种是 {currency}, 报告统一用 USD. 请手动核对汇率
                </div>
              )}
            </label>
            <label className="text-xs sm:col-span-2">
              <span className="text-ink-2">案件背景说明 (自由文本, 可空)</span>
              <textarea
                rows={3}
                value={caseNote}
                onChange={(e) => setCaseNote(e.target.value)}
                maxLength={2000}
                placeholder="例: 客户于 2026-08-01 提货后 21 天仍未付尾款 USD 12,500, 多次邮件 / 电话催讨失联"
                className="mt-1 w-full px-2 py-1 border border-line rounded text-xs bg-surface"
              />
            </label>
          </div>
        </div>

        {/* 3. 时间线 */}
        <div className="p-4 border-b border-line">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-ink-2">
              3. 事件时间线 ({timeline.length})
            </div>
            <Button variant="ghost" size="sm" iconLeft="plus" onClick={addTimelineRow}>
              加一条
            </Button>
          </div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {timeline.map((t, i) => (
              <div key={`t-${i}-${t.date || 'empty'}`} className="flex items-center gap-1.5">
                <input
                  type="date"
                  value={t.date}
                  onChange={(e) => updateTimeline(i, { date: e.target.value })}
                  className="px-1.5 py-1 border border-line rounded text-xs bg-surface w-32"
                />
                <input
                  type="text"
                  value={t.type}
                  onChange={(e) => updateTimeline(i, { type: e.target.value })}
                  placeholder="事件类型 (例: PI 发出)"
                  className="flex-1 min-w-0 px-2 py-1 border border-line rounded text-xs bg-surface"
                  maxLength={50}
                />
                <input
                  type="text"
                  value={t.description}
                  onChange={(e) => updateTimeline(i, { description: e.target.value })}
                  placeholder="描述"
                  className="flex-1 min-w-0 px-2 py-1 border border-line rounded text-xs bg-surface"
                  maxLength={200}
                />
                {timeline.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTimeline(i)}
                    className="p-1 text-ink-3 hover:text-bad"
                    aria-label="删除"
                  >
                    <Icon name="x" size="xs" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 4. 证据 */}
        <div className="p-4 border-b border-line">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-ink-2">4. 证据清单 ({evidence.length})</div>
            <Button variant="ghost" size="sm" iconLeft="plus" onClick={addEvidenceRow}>
              加一条
            </Button>
          </div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {evidence.map((e, i) => (
              <div key={`e-${i}-${e.name || 'empty'}`} className="flex items-center gap-1.5">
                <select
                  value={e.type}
                  onChange={(ev) =>
                    updateEvidence(i, { type: ev.target.value as SinosureEvidence['type'] })
                  }
                  className="px-1.5 py-1 border border-line rounded text-xs bg-surface"
                >
                  {EVIDENCE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={e.name}
                  onChange={(ev) => updateEvidence(i, { name: ev.target.value })}
                  placeholder="证据名 (例: PI-2026-001)"
                  className="flex-1 min-w-0 px-2 py-1 border border-line rounded text-xs bg-surface"
                  maxLength={100}
                />
                <input
                  type="text"
                  value={e.location ?? ''}
                  onChange={(ev) => updateEvidence(i, { location: ev.target.value })}
                  placeholder="位置 (可选)"
                  className="flex-1 min-w-0 px-2 py-1 border border-line rounded text-xs bg-surface"
                  maxLength={200}
                />
                {evidence.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEvidence(i)}
                    className="p-1 text-ink-3 hover:text-bad"
                    aria-label="删除"
                  >
                    <Icon name="x" size="xs" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 错误 */}
        {error && (
          <div className="p-3 bg-bad/5 border-b border-line text-xs text-bad">
            <Icon name="alert-circle" size="xs" className="inline mr-1" /> {error}
          </div>
        )}

        {/* 操作按钮 */}
        <div className="p-4 border-b border-line flex flex-wrap items-center gap-2">
          <Button
            variant="danger"
            size="sm"
            iconLeft="siren"
            loading={loading}
            onClick={handleGenerate}
          >
            生成信保报案
          </Button>
          {report && (
            <Button variant="primary" size="sm" iconLeft="download" onClick={handleDownload}>
              下载 {report.filename}
            </Button>
          )}
        </div>

        {/* 报告预览 */}
        {report && (
          <div className="p-4 max-h-72 overflow-y-auto">
            <div className="text-xs text-ink-3 mb-2">
              📄 {report.incidentLabel} — {report.filename}
            </div>
            <pre className="text-xs text-ink-2 whitespace-pre-wrap font-sans leading-relaxed bg-surface-2 p-3 rounded">
              {report.markdown}
            </pre>
          </div>
        )}

        {/* 底部说明 */}
        <div className="p-3 bg-surface-2 border-t border-line text-[11px] text-ink-3 leading-relaxed">
          💡 报案材料会写一份到 compliance_event (type=sinosure_report) 留底.
          实际提交前请人工核实所有数据. 模板来自 docs/ANTI-FRAUD.md Part 6.
        </div>
      </div>
    </div>
  );
}
